import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CFlatList } from '@app/components/Common/';

import { TradeDetailListSection, TradeText } from './styles';

import TradeDetailListHeader from './TradeDetailListHeader';
import TradeDetailListItem from './TradeDetailListItem';
import TradeDetailError from './TradeDetailError';

import { useFetcher } from '@app/hooks/fetcher';
import { AuthCheckText } from '@app/components';
import { useToken } from '@app/hooks';
import { Text } from '@/styles/reactnative.tsx';
import BigNumber from 'bignumber.js';
import { COLORS } from '@app/constants';
import KrwPrice from '@components/KrwPrice.tsx';
import EntryTime from '@components/EntryTime.tsx';
import { Table } from 'antd';
import { useTranslation } from 'react-i18next';
import { formatDecimal4Truncate } from '@utils/formatDecimal.tsx';
import { truncateWithComma } from '@utils/Truncate.ts';
import useScrollLayout from '../useScrollLayout';
import { useExchangeStore } from '@/app/hooks/trade/exchangeStore';
import ContractsColumn from '../Columns/ContractsColumn';
import EmptyPosition from '@/app/components/EmptyPosition';

// 헤더 스타일 (TradePositionsTable과 동일)
const headerStyle = {
  fontFamily: 'NanumSquareRound',
  fontSize: 12,
  color: '#b3b5bd',
  letterSpacing: '-0.24px',
  whiteSpace: 'nowrap' as const,
};

// 셀 데이터 공통 스타일
const cellStyle = {
  display: 'flex',
  letterSpacing: 0,
  whiteSpace: 'nowrap' as const,
  height: 17,
  alignItems: 'flex-end' as const,
  color: '#fff',
  fontFamily: 'NanumSquareRound',
  fontSize: 12,
  lineHeight: 1,
};

// 숫자 강조 스타일
const cellStyleBold = {
  fontWeight: 700,
  fontFamily: 'NanumSquareRound',
  letterSpacing: 0,
  whiteSpace: 'nowrap' as const,
  color: '#fff',
  lineHeight: 'normal',
  fontSize: 13,
};

const TradeOrderHistoryTable = ({ parentHeight }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { data: token } = useToken();

  const { t } = useTranslation();
  const { EPs } = useExchangeStore();

  const KEYURL = '/trade/orderlist?status=*&ep_id=*';

  const { data, isError } = useFetcher(KEYURL, false);

  const list = data?.result?.result?.list;
  const { headerHeight, needsScroll } = useScrollLayout(
    containerRef,
    list,
    parentHeight
  );

  // const dataType = [
  //   "contracts",
  //   "amount",
  //   "orderPrice",
  //   "tradeType",
  //   "orderType",
  //   "margin",
  //   "fee",
  //   "orderTime",
  //   "status",
  // ];

  const columns = [
    {
      width: '11.1%',
      dataIndex: '',
      title: <span style={headerStyle}>{t('contracts')}</span>,
      render: (value, data) => (
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-start',
          }}
        >
          <ContractsColumn item={data} />
        </div>
      ),
    },
    {
      width: '11.1%',
      dataIndex: '',
      title: <span style={headerStyle}>{t('amount')}</span>,
      render: (value, data) => {
        const EP = EPs?.find((ep: any) => +ep.id === +data?.exchange_pairs_id);
        const coin = data?.pairs?.split('/')[0];
        return (
          <div style={{ ...cellStyleBold }}>
            {BigNumber(data?.amount)
              .multipliedBy(data?.leverage)
              .toFormat(EP?.dp2 || 3)}{' '}
            {coin}
          </div>
        );
      },
    },
    // {
    //   dataIndex: '',
    //   title: t('filledPrice'),
    //   render: (value, data) => {
    //     const currency = data?.pairs?.split("/")[1];
    //     return (
    //       <TradeText style={{whiteSpace: 'nowrap', justifyContent: 'flex-start'}}>
    //         {data?.trade_price || data?.order_price} {currency}
    //       </TradeText>
    //     );
    //   },
    // },
    {
      width: '11.1%',
      dataIndex: '',
      title: <span style={headerStyle}>{t('orderPrice')}</span>,
      render: (value, data) => {
        const EP = EPs?.find((ep: any) => +ep.id === +data?.exchange_pairs_id);
        const dp = EP?.dp || 3;

        return (
          <div style={{ ...cellStyle }}>
            {data?.limit_market !== 'M'
              ? BigNumber(data?.order_price).toFormat(dp)
              : '-'}
          </div>
        );
      },
    },
    {
      width: '11.1%',
      dataIndex: '',
      title: <span style={headerStyle}>{t('conclusionPrice')}</span>,
      render: (value, data) => {
        const EP = EPs?.find((ep: any) => +ep.id === +data?.exchange_pairs_id);
        const dp = EP?.dp || 3;

        return (
          <div style={{ ...cellStyle }}>
            {(data?.status === 1 || data?.status === 2) && data?.trade_price
              ? BigNumber(data?.trade_price).toFormat(dp)
              : '-'}
          </div>
        );
      },
    },
    {
      width: '11.1%',
      dataIndex: '',
      title: <span style={headerStyle}>{t('orderType')}</span>,
      render: (value, data) => {
        return (
          <div style={{ ...cellStyle }}>
            {data?.limit_market === 'L' ? t('limit') : t('market')}{' '}
            {data?.data?.is_triggered && ` (${t('trigger')})`}
          </div>
        );
      },
    },
    {
      width: '11.1%',
      dataIndex: '',
      title: <span style={headerStyle}>{t('margin')}</span>,
      render: (value, data) => {
        const currency = data?.pairs?.split('/')[1];
        return (
          <div style={{ ...cellStyle }}>
            {truncateWithComma(data?.margin, 2)} {currency}
          </div>
        );
      },
    },
    {
      width: '11.1%',
      dataIndex: '',
      title: <span style={headerStyle}>{t('fee')}</span>,
      render: (value, data) => {
        const currency = data?.pairs?.split('/')[1];
        return (
          <div style={{ ...cellStyle, minWidth: 80 }}>
            <KrwPrice
              dp={2}
              price={formatDecimal4Truncate(
                -BigNumber(data?.fee_price).toNumber()
              )}
              currency={currency}
              equiv={false}
            />
          </div>
        );
      },
    },
    {
      width: '11.1%',
      dataIndex: '',
      title: <span style={headerStyle}>{t('orderTime')}</span>,
      render: (value, data) => {
        return (
          <EntryTime
            style={{ ...cellStyle, justifyContent: 'left' }}
            time={data?.created_at}
          />
        );
      },
    },
    {
      width: '11.1%',
      dataIndex: '',
      title: <span style={headerStyle}>{t('status')}</span>,
      render: (value, data) => {
        return (
          <div style={{ ...cellStyle }}>
            {data?.status === 1
              ? t('opened')
              : data?.status === 2
                ? t('filled')
                : t('canceled')}
          </div>
        );
      },
    },
  ];

  // const renderItem = (item: any, index: any) => {
  //   return (
  //     <TradeDetailListItem
  //       item={item}
  //       index={index}
  //       dataType={dataType.length}
  //     />
  //   );
  // };

  return (
    <TradeDetailListSection ref={containerRef}>
      {/*<TradeDetailListHeader dataType={dataType} />*/}
      {isError ? <TradeDetailError /> : null}

      {token ? null : <AuthCheckText />}

      {list?.length ? (
        <Table
          columns={columns}
          dataSource={list}
          pagination={false}
          style={{ width: '100%', height: '100%' }}
          sticky={true}
          size="small"
          scroll={
            needsScroll
              ? {
                  x: 'max-content',
                  y: parentHeight - (headerHeight || 38) - 20, // 실제 헤더 높이 + 여백
                }
              : {
                  x: 'max-content',
                }
          }
        />
      ) : (
        <EmptyPosition type="orderHistory" />
      )}
      {/*{list?.length ? <CFlatList data={list} renderItem={renderItem} /> : null}*/}
    </TradeDetailListSection>
  );
};

export default TradeOrderHistoryTable;
